import { isSafeMode } from '@/utils/safeMode';
import { ref } from 'vue';

/**
 * Composable for clearing all application storage
 */
export function useClearStorage() {
    const showDialog = ref(false);
    const isClearing = ref(false);
    const showSuccessDialog = ref(false);
    const showErrorDialog = ref(false);

    /**
     * Clears all localStorage data
     */
    const clearLocalStorage = () => {
        try {
            localStorage.clear();
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    };

    /**
     * Clears all sessionStorage data
     */
    const clearSessionStorage = () => {
        try {
            sessionStorage.clear();
        } catch (error) {
            console.error('Failed to clear sessionStorage:', error);
        }
    };

    /**
     * Clears all IndexedDB databases
     */
    const clearIndexedDB = async () => {
        try {
            if ('indexedDB' in window) {
                // Get all databases
                const databases = await indexedDB.databases();

                // Delete each database
                for (const db of databases) {
                    if (db.name) {
                        await new Promise<void>((resolve, reject) => {
                            const deleteRequest = indexedDB.deleteDatabase(db.name!);
                            deleteRequest.onsuccess = () => resolve();
                            deleteRequest.onerror = () => reject(deleteRequest.error);
                            deleteRequest.onblocked = () => {
                                console.warn(`Database ${db.name} deletion blocked`);
                                resolve(); // Continue even if blocked
                            };
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Failed to clear IndexedDB:', error);
        }
    };

    /**
     * Clears all browser cache
     */
    const clearBrowserCache = async () => {
        try {
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map(cacheName => caches.delete(cacheName))
                );
            }
        } catch (error) {
            console.error('Failed to clear browser cache:', error);
        }
    };

    /**
     * Clears all application storage (localStorage, sessionStorage, IndexedDB, and browser cache)
     */
    const clearAllStorage = async () => {
        showDialog.value = true;
    };

    /**
     * Performs the actual clearing of storage
     */
    const performClearStorage = async () => {
        isClearing.value = true;
        try {
            // Clear all storage types
            clearLocalStorage();
            clearSessionStorage();
            await clearIndexedDB();
            await clearBrowserCache();

            // Close the dialog and show success
            showDialog.value = false;
            isClearing.value = false;

            // Reload the page to ensure clean state (skip in safe mode to avoid reload loop)
            if (!isSafeMode()) {
                window.location.reload();
            }
        } catch (error) {
            console.error('Failed to clear all storage:', error);
            isClearing.value = false;
            showDialog.value = false;
            showErrorDialog.value = true;
        }
    };

    /**
     * Cancels the clear storage operation
     */
    const cancelClearStorage = () => {
        showDialog.value = false;
    };

    return {
        clearLocalStorage,
        clearSessionStorage,
        clearIndexedDB,
        clearBrowserCache,
        clearAllStorage,
        performClearStorage,
        cancelClearStorage,
        showDialog,
        isClearing,
        showSuccessDialog,
        showErrorDialog,
    };
}
